import { decodeProtectedHeader, importSPKI, jwtVerify, type JWTPayload } from 'jose';
import { ACTIVATION_AUDIENCE, ACTIVATION_ISSUER } from './activationConstants';
import { resolvePublicKey } from './PublicKeyRegistry';

/** Types de jetons signés par le serveur de licence (claim `typ`). */
export type ServerTokenType = 'entitlements' | 'status';

export type ServerTokenResult = { ok: true; payload: JWTPayload } | { ok: false; reason: string };

export interface ServerTokenOptions {
  /** Empreinte locale : le jeton doit être émis pour CE poste. */
  deviceFingerprint: string;
  /** Horloge (secondes) — injectable pour les tests. */
  nowSec?: number;
  keyResolver?: (kid: string | undefined) => string | null;
}

/**
 * Vérifie un JWT ES256 émis par le serveur de licence (droits, statut).
 * Algorithme épinglé, émetteur, audience, type, empreinte poste et expiration contrôlés.
 * Aucune valeur n'est jamais lue d'un jeton non vérifié.
 */
export async function verifyServerToken(
  jwt: unknown,
  expectedType: ServerTokenType,
  options: ServerTokenOptions,
): Promise<ServerTokenResult> {
  if (typeof jwt !== 'string' || jwt.split('.').length !== 3) {
    return { ok: false, reason: 'absent_ou_mal_forme' };
  }
  try {
    const { kid, alg } = decodeProtectedHeader(jwt);
    if (alg !== 'ES256') return { ok: false, reason: 'algorithme_refuse' };
    const pem = (options.keyResolver ?? resolvePublicKey)(kid);
    if (!pem) return { ok: false, reason: 'cle_inconnue' };
    const key = await importSPKI(pem, 'ES256');
    const nowSec = options.nowSec ?? Math.floor(Date.now() / 1000);
    const { payload } = await jwtVerify(jwt, key, {
      algorithms: ['ES256'],
      issuer: ACTIVATION_ISSUER,
      audience: ACTIVATION_AUDIENCE,
      currentDate: new Date(nowSec * 1000),
      clockTolerance: 300,
      requiredClaims: ['exp', 'iat'],
    });
    if (payload.typ !== expectedType) return { ok: false, reason: 'type_incorrect' };
    if (payload.device_fingerprint !== options.deviceFingerprint) {
      return { ok: false, reason: 'autre_poste' };
    }
    return { ok: true, payload };
  } catch (err) {
    const code = (err as { code?: string }).code ?? 'signature_invalide';
    return { ok: false, reason: code };
  }
}
